/**
 * Task Completion Progress
 *
 * Loads aggregate subtask and checklist completion for Space tasks.
 *
 * Primary responsibilities:
 * - Call the database aggregate RPC for task completion counts
 * - Normalize count rows into a task-id keyed map
 *
 * Dependencies:
 * - Supabase RPC client
 */

import type { SupabaseClient } from "@supabase/supabase-js"

export type TaskCompletionProgress = {
  subtask_items: number
  subtask_completed: number
  checklist_items: number
  checklist_completed: number
}

type CompletionProgressRpcClient = {
  rpc: (
    fn: "get_space_task_completion_progress",
    args: {
      p_space_id: string
      p_task_ids: string[]
    }
  ) => Promise<{
    data: unknown
    error: { message: string } | null
  }>
}

const EMPTY_PROGRESS: TaskCompletionProgress = {
  subtask_items: 0,
  subtask_completed: 0,
  checklist_items: 0,
  checklist_completed: 0
}

// --------------

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null

// --------------

const readString = (value: unknown) =>
  typeof value === "string" && value.trim().length > 0 ? value : null

// --------------

const readCount = (value: unknown) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value
  }

  if (typeof value === "string") {
    const count = Number(value)
    return Number.isFinite(count) ? count : 0
  }

  return 0
}

// --------------

export const getEmptyTaskCompletionProgress = (): TaskCompletionProgress => ({
  ...EMPTY_PROGRESS
})

// --------------

export const getTaskCompletionProgressByTaskId = async (
  client: SupabaseClient,
  spaceId: string,
  taskIds: string[]
) => {
  if (taskIds.length === 0) {
    return new Map<string, TaskCompletionProgress>()
  }

  const rpcClient = client as unknown as CompletionProgressRpcClient
  const { data, error } = await rpcClient.rpc(
    "get_space_task_completion_progress",
    {
      p_space_id: spaceId,
      p_task_ids: taskIds
    }
  )

  if (error) {
    throw new Error(error.message)
  }

  const rows = Array.isArray(data) ? data : []
  const progressByTaskId = new Map<string, TaskCompletionProgress>()

  for (const row of rows) {
    if (!isRecord(row)) {
      continue
    }

    const taskId = readString(row.task_id)

    if (!taskId) {
      continue
    }

    progressByTaskId.set(taskId, {
      subtask_items: readCount(row.subtask_items),
      subtask_completed: readCount(row.subtask_completed),
      checklist_items: readCount(row.checklist_items),
      checklist_completed: readCount(row.checklist_completed)
    })
  }

  return progressByTaskId
}
